import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPermissions } from '.';
import { AccessLevel } from './authInterfaces';
import { fetchAuthLocalStorageValue } from './authLocalStorageService';

interface IAuthState {
  inProcess: boolean;
  itOverridePermissions?: IPermissions;
  itOverrideUser?: { sid: string; name: string };
  loggedIn: boolean;
  loginFailed: boolean;
  permissions: IPermissions;
}

const initialState: IAuthState = {
  inProcess: false,
  itOverridePermissions: fetchAuthLocalStorageValue('auth/itOverridePermissions'),
  itOverrideUser: fetchAuthLocalStorageValue('auth/itOverrideUser'),
  loggedIn: false,
  loginFailed: false,
  permissions: {
    dealerPortal: { accessLevel: AccessLevel.None },
    finance: { accessLevel: AccessLevel.None },
    globalAdmin: false,
    it: { accessLevel: AccessLevel.None },
    logistics: { accessLevel: AccessLevel.None, warehouseGroupIds: [], warehouses: [] },
    marketing: { accessLevel: AccessLevel.None },
    operations: { accessLevel: AccessLevel.None, warehouseGroupIds: [], warehouses: [] },
    sales: { accessLevel: AccessLevel.None, regions: [], salesDistricts: [] }
  }
};

// tslint:disable: no-empty
const authSlice = createSlice({
  initialState,
  name: 'auth',
  reducers: {
    fetchItImpersonatePermissions: {
      prepare: (sid: string) => ({ payload: { sid } }),
      reducer: (state, action: PayloadAction<{ sid: string }>) => {
        state.inProcess = true;
      }
    },
    fetchPermissions: {
      prepare: (removeItOverrides?: boolean) => ({ payload: { removeItOverrides } }),
      reducer: (state, action: PayloadAction<{ removeItOverrides?: boolean }>) => {
        state.inProcess = true;
      }
    },
    login: () => {},
    logout: {
      prepare: (complete: boolean = true) => ({ payload: { complete } }),
      reducer: (state, action: PayloadAction<{ complete: boolean }>) => {}
    },
    resetItOverides: state => {
      state.itOverridePermissions = undefined;
      state.itOverrideUser = undefined;
    },
    setItOverrides: {
      prepare: (permissions: Partial<IPermissions>, user?: { sid: string; name: string }) => ({
        payload: { permissions, user }
      }),
      reducer: (
        state,
        action: PayloadAction<{ permissions: Partial<IPermissions>; user?: { sid: string; name: string } }>
      ) => {
        state.inProcess = false;

        const itOverrides = state.itOverridePermissions ?? {};
        state.itOverridePermissions = {
          ...state.permissions,
          ...itOverrides,
          ...action.payload.permissions,
          it: state.permissions.it
        };

        state.itOverrideUser = action.payload.user;
      }
    },
    setLoggedIn: state => {
      state.loggedIn = true;
    },
    setLoggedOut: state => {
      state.loggedIn = false;
    },
    setPermissions: (state, action: PayloadAction<IPermissions>) => {
      state.permissions = action.payload;
    },
    toggleLoggingIn: state => {
      state.inProcess = !state.inProcess;
    },
    toggleLoginFailed: state => {
      state.loginFailed = !state.loginFailed;
    }
  }
});

export const { actions: authActionCreators, reducer: authReducer } = authSlice;
